<template>
  <div
    class="fill-height doc-page"
    :style="$vuetify.breakpoint.name !== 'xs' && $vuetify.breakpoint.name !== 'sm' ? `padding-bottom: 0px; padding-top: 62px;` : `padding-bottom: 610px; padding-top: 64px;`">
    <v-layout wrap>
      <v-flex>
        <section>
          <v-container>
            <v-row>
              <v-col cols="12" md="9">
                <div class="detail-breadcrumb">
                  <router-link to="/propemperda" :style="`color: ${set_color}`">Propemperda</router-link>
                  <v-icon size="14" color="#B0B4BA" class="mx-1">mdi-chevron-right</v-icon>
                  <span>Detail Propemperda</span>
                </div>

                <div v-if="process.load">
                  <v-card flat class="doc-detail-card">
                    <v-skeleton-loader
                      :loading="process.load"
                      type="article, list-item-three-line, list-item-three-line"
                      class="ma-auto">
                    </v-skeleton-loader>
                  </v-card>
                </div>

                <v-card flat class="doc-detail-card pa-4 pt-0" v-if="!process.load && Object.keys(propem).length > 0">
                  <v-card-title class="detail-title px-0 mb-0">
                    {{ propem.title }}
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <div class="doc-card__meta">
                      <span class="doc-card__meta-item">
                        <v-icon size="14" color="#8A8F98">mdi-calendar-range</v-icon>
                        Tahun {{ propem.year }}
                      </span>
                      <span class="doc-card__meta-item">
                        <v-icon size="14" color="#8A8F98">mdi-clock-outline</v-icon>
                        {{ propem.input_datetime | date }}
                      </span>
                    </div>
                  </v-card-text>

                  <v-divider class="my-5"></v-divider>

                  <div v-if="!process.loadDetail && stages.length > 0">
                    <div
                      v-for="(stage, stageIndex) in stages"
                      :key="stage.key"
                      class="mb-7">
                      <div class="d-flex align-center mb-3">
                        <div class="stage-number" :style="`background-color: ${set_color}`">
                          {{ stageIndex + 1 }}
                        </div>
                        <div class="stage-label" :style="`color: ${set_color}`">
                          {{ stage.label }}
                        </div>
                      </div>

                      <div class="stage-list">
                        <div class="stage-list__head">
                          <div class="stage-list__col stage-list__col--no">No</div>
                          <div class="stage-list__col stage-list__col--title">Judul</div>
                          <div class="stage-list__col stage-list__col--date">Tanggal</div>
                          <div class="stage-list__col stage-list__col--action">Dokumen</div>
                        </div>
                        <div
                          v-for="(file, fileIndex) in stage.items"
                          :key="`${stage.key}-${fileIndex}`"
                          class="stage-list__row">
                          <div class="stage-list__col stage-list__col--no text-second">
                            <span class="stage-index">{{ fileIndex + 1 }}</span>
                          </div>
                          <div class="stage-list__col stage-list__col--title">
                            <div class="stage-file-title">{{ file.title }}</div>
                          </div>
                          <div class="stage-list__col stage-list__col--date text-second">
                            <span class="stage-file-date">{{ file.date | date }}</span>
                          </div>
                          <div class="stage-list__col stage-list__col--action">
                            <template v-if="file.url">
                              <div class="stage-actions">
                                <a
                                  :href="file.url"
                                  target="_blank"
                                  rel="noopener"
                                  class="stage-action stage-action--primary">
                                  <v-icon size="16">mdi-eye-outline</v-icon>
                                  <span>Lihat</span>
                                </a>
                                <button
                                  type="button"
                                  class="stage-action"
                                  @click="downloadFile(file.url, file.title)">
                                  <v-icon size="16">mdi-download</v-icon>
                                  <span>Unduh</span>
                                </button>
                              </div>
                            </template>
                            <span v-else class="stage-empty">File belum tersedia</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Empty
                    v-if="!process.loadDetail && stages.length < 1"
                    class="py-8"
                    margin="my-8"
                    size="125"
                    message="Detail Propemperda" />
                </v-card>

                <Empty
                  v-if="!process.load && Object.keys(propem).length < 1"
                  class="py-12"
                  margin="my-16"
                  size="125"
                  message="Propemperda" />
              </v-col>
              <v-col cols="12" md="3">
                <Sidebar />
              </v-col>
            </v-row>
          </v-container>
        </section>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
  import { createSEOMeta } from '@/utils/seo'

  const STAGE_FIELDS = [
    { key: 'penyusunan_naskah_array', label: 'Penyusunan Naskah' },
    { key: 'penyusunan_raperda_array', label: 'Penyusunan Raperda' },
    { key: 'konsultasi_public_array', label: 'Konsultasi Publik' },
    { key: 'harmonisasi_bamperda_array', label: 'Harmonisasi Bamperda' },
    { key: 'praharmonisasi_array', label: 'Praharmonisasi' },
    { key: 'harmonisasi_kemenkum_array', label: 'Harmonisasi Kemenkum' },
    { key: 'rapat_paripurna_penyampaian_array', label: 'Rapat Paripurna Penyampaian' },
    { key: 'pandangan_umum_fraksi_array', label: 'Pandangan Umum Fraksi' },
    { key: 'jawaban_kepala_daerah_array', label: 'Jawaban Kepala Daerah' },
    { key: 'pembentukan_pansus_array', label: 'Pembentukan Pansus' },
    { key: 'rapat_pansus_dprd_array', label: 'Rapat Pansus DPRD' },
    { key: 'finalisasi_raperda_dprd_array', label: 'Finalisasi Raperda DPRD' },
    { key: 'evaluasi_gubernur_array', label: 'Evaluasi Gubernur' },
    { key: 'penyempurnaan_hasil_evaluasi_array', label: 'Penyempurnaan Hasil Evaluasi' },
    { key: 'rapat_paripurna_bersama_array', label: 'Rapat Paripurna Bersama' },
    { key: 'penetapan_pengundangan_array', label: 'Penetapan Pengundangan' },
    { key: 'penyebarluasan_array', label: 'Penyebarluasan' }
  ]

  export default {
    middleware: false,
    data () {
      return {
        propem: {},
        detail: {},
        process: {
          load: false,
          loadDetail: false
        },
        message: {
          success: '',
          error: ''
        },
        content: {
          url: '/propemperda/:slug',
          title: 'Detail Propemperda',
          description: 'Detail Program Pembentukan Peraturan Daerah JDIH Setwan Kabupaten Klaten',
          image: require('@/assets/logo/favicon.png')
        }
      }
    },
    head () {
      return {
        title: this.propem.title || this.content.title,
        meta: createSEOMeta({
          ...this.content,
          title: this.propem.title || this.content.title,
          description: this.propem.title || this.content.description
        })
      }
    },
    components: {
      Empty: () => import(/* webpackPrefetch: true */ '@/components/Empty'),
      Sidebar: () => import(/* webpackPrefetch: true */ '@/components/sidebar/index')
    },
    computed: {
      rps () {
        return this.$vuetify.breakpoint.name
      },
      set_color () {
        return this.$store.state.header.config.color
      },
      stages () {
        if (!this.detail || Object.keys(this.detail).length < 1) {
          return []
        }

        return STAGE_FIELDS
          .map(stage => {
            const items = Array.isArray(this.detail[stage.key]) ? this.detail[stage.key] : []
            return {
              ...stage,
              items
            }
          })
          .filter(stage => stage.items.length > 0)
      }
    },
    mounted () {
      this.fetch()
    },
    methods: {
      downloadFile (fileURL, fileName) {
        const link = document.createElement('a')
        link.href = fileURL
        link.target = '_blank'
        link.download = fileName || 'file'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      async fetch () {
        this.process.load = true
        this.propem = {}
        this.detail = {}

        const id = this.$route.query.q
        let params = {}

        if (id) {
          params.id = id
        } else if (this.$route.params.slug) {
          params.title = this.$route.params.slug.split('-').join(' ').split('=').join('-').split('>').join('/')
        }

        await this.$axios.$get(`${process.env.API}sites/propem/data`, { params })
          .then(response => {
            let res = response

            if (res.status === 200 && res.results.data.length > 0) {
              this.propem = res.results.data[0]
              this.fetchDetail(this.propem.id)
            } else if (id) {
              this.fetchById(id)
            } else {
              this.process.load = false
            }
          })
          .catch(() => {
            this.process.load = false
          })
      },
      async fetchById (id) {
        this.process.load = true

        await this.$axios.$get(`${process.env.API}sites/propem/data`, { params: { id } })
          .then(response => {
            let res = response

            if (res.status === 200 && res.results.data.length > 0) {
              this.propem = res.results.data[0]
              this.fetchDetail(this.propem.id)
            } else {
              this.process.load = false
            }
          })
          .catch(() => {
            this.process.load = false
          })
      },
      async fetchDetail (propemId) {
        this.process.loadDetail = true

        await this.$axios.$get(`${process.env.API}sites/propem/details/data`, {
          params: { propem_id: propemId }
        })
          .then(response => {
            let res = response

            this.process.load = false
            this.process.loadDetail = false

            if (res.status === 200 && res.results.data.length > 0) {
              this.detail = res.results.data[0]
            } else {
              this.detail = {}
            }
          })
          .catch(() => {
            this.process.load = false
            this.process.loadDetail = false
            this.detail = {}
          })
      }
    }
  }
</script>

<style scoped>
.doc-page {
  background-color: #F7F8FA;
}
.detail-breadcrumb {
  font-size: 13px;
  color: #8A8F98;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}
.detail-breadcrumb a {
  font-weight: 600;
  text-decoration: none;
}
.doc-detail-card {
  border: 1px solid #E7E9EC;
  border-radius: 10px;
}
.detail-title {
  font-size: 18px;
  font-weight: 700;
  color: #1F2328;
  line-height: 1.4;
}
.doc-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}
.doc-card__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #71767D;
}
.stage-number {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex: none;
}
.stage-label {
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.stage-list {
  border: 1px solid #E6EAF0;
  border-radius: 14px;
  overflow: hidden;
  background: #FFFFFF;
}
.stage-list__head {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #F8FAFC;
  color: #69707A;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.stage-list__row {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-top: 1px solid #EEF2F6;
  transition: background 0.2s ease, transform 0.2s ease;
}
.stage-list__row:hover {
  background: #FBFCFE;
}
.stage-list__col {
  font-size: 12px;
  color: #1F2328;
}
.stage-list__col--no {
  width: 44px;
  flex: none;
}
.stage-list__col--title {
  flex: 1;
  min-width: 0;
  padding-right: 16px;
}
.stage-list__col--date {
  width: 140px;
  flex: none;
}
.stage-list__col--action {
  width: 230px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.stage-index {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: #F3F6FA;
  color: #5F6874;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
}
.stage-file-title {
  color: #1F2328;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}
.stage-file-date {
  display: inline-block;
  color: #69707A;
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.stage-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.stage-action {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #DDE4EC;
  border-radius: 10px;
  background: #FFFFFF;
  color: #4F5A67;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}
.stage-action--primary {
  background: #F3F8FF;
  border-color: #D7E6FB;
  color: #2E69B3;
}
.stage-action:hover {
  transform: translateY(-1px);
  background: #F8FAFC;
  border-color: #CDD7E3;
}
.stage-action--primary:hover {
  background: #EAF3FF;
  border-color: #C6DCF9;
  color: #245B9B;
}
.stage-action:focus,
.stage-action:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(46, 125, 225, 0.14);
}
.stage-action:active {
  transform: translateY(0);
}
.stage-empty {
  color: #98A1AC;
  font-size: 12.5px;
}
@media (max-width: 900px) {
  .stage-list__head {
    display: none;
  }
  .stage-list__row {
    flex-wrap: wrap;
    gap: 10px 0;
    padding: 16px;
  }
  .stage-list__col--no {
    display: none;
  }
  .stage-list__col--title {
    width: 100%;
    order: 1;
    padding-right: 0;
  }
  .stage-list__col--date {
    order: 2;
    width: auto;
  }
  .stage-list__col--action {
    order: 3;
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
  .stage-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  .stage-action {
    flex: 1 1 140px;
  }
}
</style>
